import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Upload, X, Check, Link } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'image_url') {
      setImagePreview(value);
      setImageError(false);
    }
  };

  // Upload to Cloudinary for persistent storage
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, WEBP)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // Show immediate local preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result);
    reader.readAsDataURL(file);

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      // Get token for auth
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Cloudinary returns the full CDN URL
      const imageUrl = response.data.url;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      setImagePreview(imageUrl);
      setImageError(false);
      toast.success('Image uploaded to cloud storage!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
      setImagePreview(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const clearImage = () => {
    setFormData((prev) => ({ ...prev, image_url: '' }));
    setImagePreview(null);
    setImageError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', image_url: '' });
    setEditingCategory(null);
    setImagePreview(null);
    setImageError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      image_url: category.image_url,
    });
    setImagePreview(category.image_url);
    setImageError(false);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a category name');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, formData);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/categories`, formData);
        toast.success('Category created successfully');
      }
      
      fetchCategories();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"?`)) return;

    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-xl font-bold">Categories ({categories.length})</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-accent" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload Section */}
              <div className="space-y-2">
                <Label>Category Image</Label>
                <div className="border-2 border-dashed border-border rounded-lg overflow-hidden">
                  {imagePreview || formData.image_url ? (
                    <div className="relative group">
                      <img
                        src={imagePreview || formData.image_url}
                        alt="Preview"
                        className="w-full h-40 object-cover"
                        onError={() => setImageError(true)}
                      />
                      {/* Success indicator for Cloudinary URLs */}
                      {formData.image_url && formData.image_url.includes('cloudinary') && !imageError && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          Cloud stored
                        </div>
                      )}
                      {/* Upload progress overlay */}
                      {uploading && (
                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                          <Loader2 className="h-8 w-8 text-white animate-spin mb-2" />
                          <span className="text-white text-sm">{uploadProgress}%</span>
                        </div>
                      )}
                      {/* Hover actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Change
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={clearImage}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center py-8 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="h-10 w-10 text-primary animate-spin mb-2" />
                          <span className="text-sm text-muted-foreground">Uploading to cloud... {uploadProgress}%</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-primary mb-2" />
                          <p className="text-sm font-medium text-foreground">Click to upload image</p>
                          <p className="text-xs text-muted-foreground mt-1">Images stored permanently in cloud</p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                
                {/* URL Input */}
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      placeholder="Or paste image URL"
                      className="pl-10"
                      data-testid="category-image-input"
                    />
                  </div>
                </div>
                
                {/* Cloud storage indicator */}
                {formData.image_url && formData.image_url.includes('cloudinary') && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Image stored in cloud - will persist across deployments
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Category Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter category name"
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  data-testid="category-description-input"
                />
              </div>
              <DialogFooter className="gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit" className="btn-accent" disabled={submitting || uploading} data-testid="category-submit-btn">
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      {editingCategory ? 'Update Category' : 'Add Category'}
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <Card key={category.id} className="overflow-hidden group" data-testid={`category-card-${category.id}`}>
            <div className="relative h-40">
              <img
                src={category.image_url || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
                alt={category.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <h3 className="absolute bottom-3 left-3 font-serif text-lg font-bold text-white">
                {category.name}
              </h3>
              {/* Cloud indicator on card */}
              {category.image_url && category.image_url.includes('cloudinary') && (
                <div className="absolute top-2 right-2 bg-green-500/90 text-white px-2 py-0.5 rounded text-xs">
                  Cloud
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {category.description || 'No description'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditDialog(category)}
                  data-testid={`edit-category-${category.id}`}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDelete(category.id, category.name)}
                  data-testid={`delete-category-${category.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminCategories;
